import { useRef } from 'react';

import FORM_KEYS from 'apps/web/constants/forms/quizmaker/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import { useScrollToError } from 'apps/web/hooks/useScrollToError';
import {
  shouldDisableObjective2,
  shouldDisableObjective3,
} from 'apps/web/utils/lesson-format/objectiveFieldHelpers';

import {
  LC_PAGE_SUBJECT_CHAR_LIMIT,
  LP_PAGE_OBJECTIVE_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import { TextAreaField, TextField } from '../form-fields';
import { GradeLevelDropdown, StateDropdown } from '../Select';
import { FrameworkDropdown } from '../Select';

import processInput from '../../utils/formInput';

export default function AmericanQuizmaker({
  errors,
  watchedValues,
  setValue,
  triggerValidation,
}: {
  errors;
  watchedValues;
  setValue;
  triggerValidation;
  clearErrors;
}) {
  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const textAreaRefs = {
    objective: useRef<HTMLTextAreaElement>(null),
    objective2: useRef<HTMLTextAreaElement>(null),
    objective3: useRef<HTMLTextAreaElement>(null),
  };

  useAutosizeTextArea(textAreaRefs.objective.current, watchedValues.objective);
  useAutosizeTextArea(
    textAreaRefs.objective2.current,
    watchedValues.objective2,
  );
  useAutosizeTextArea(
    textAreaRefs.objective3.current,
    watchedValues.objective3,
  );

  useScrollToError(errors);

  return (
    <>
      <FrameworkDropdown
        error={errors.curriculumFramework}
        label="Curriculum Framework"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.curriculumFramework}
        errorMessage={
          !!errors.curriculumFramework && 'Please select a Framework'
        }
      />
      <GradeLevelDropdown
        hasCollege
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        label="Grade Level"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.gradeLevel}
      />
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id="subject"
        label="Subject Area"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name="subject"
        placeholder="Input the subject for this quiz"
        type="subject"
        value={watchedValues.subject}
        isMaxLengthReached={
          watchedValues.subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            watchedValues.subject?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            e.target.value,
          )
        }
      />
      <StateDropdown
        error={errors.specificStandards}
        label="State Standard"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.specificStandards}
        errorMessage={
          !!errors.specificStandards && 'Please select a State Standard'
        }
      />
      <div className="col-span-full">
        <TextAreaField
          showSubLabel
          className="col-span-full"
          errorMessage={!!errors.objective && 'Please enter the Objective'}
          id={FORM_KEYS.objective}
          label="Objectives"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective}
          placeholder="Input the first objective for this quiz"
          rows={2}
          subLabel={'At the end of the quiz, the students will be able to...'}
          textAreaRef={textAreaRefs.objective}
          value={watchedValues.objective}
          isMaxLengthReached={
            watchedValues.objective?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              watchedValues.objective?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              e.target.value,
            )
          }
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective2(watchedValues)}
          id={FORM_KEYS.objective2}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective2}
          placeholder="Input the second objective for this quiz"
          rows={2}
          textAreaRef={textAreaRefs.objective2}
          value={watchedValues.objective2}
          isMaxLengthReached={
            watchedValues.objective2?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              watchedValues.objective2?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              e.target.value,
            )
          }
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective3(watchedValues)}
          id={FORM_KEYS.objective3}
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name={FORM_KEYS.objective3}
          placeholder="Input the third objective for this quiz"
          rows={2}
          textAreaRef={textAreaRefs.objective3}
          value={watchedValues.objective3}
          isMaxLengthReached={
            watchedValues.objective3?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              watchedValues.objective3?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              e.target.value,
            )
          }
        />
      </div>
      <TextAreaField
        className="col-span-full"
        id={FORM_KEYS.strategies}
        label="Assessment Strategies (Optional)"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name="strategies"
        placeholder="Input what assessment to prioritize"
        type="strategies"
        value={watchedValues.strategies}
        errorMessage={
          !!errors.strategies && 'Please enter the Assessment Strategy'
        }
        isMaxLengthReached={
          watchedValues.strategies?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.strategies,
            watchedValues.strategies?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.strategies,
            e.target.value,
          )
        }
      />
    </>
  );
}
